import { Briefcase, Building2, Users, Sparkles } from "lucide-react"
import { DEF_JAM_ORG_ID } from "@/mockData/common/demoFilters"
import { defJamContacts, defJamConversations } from "@/mockData/def-jam/people"

export type ViewMode = "grid" | "list"
export type ContactFilter = "all" | "network" | "organization" | "starred"

export interface Contact {
  id: string
  name: string
  role: string
  company: string
  email: string
  phone: string
  location: string
  avatar: string | undefined
  tags: string[]
  lastContact: string
  isStarred: boolean
  type: "network" | "organization"
}

export interface Conversation {
  id: string
  contactId: string
  contactName: string
  avatar: string | undefined
  lastMessage: string
  timestamp: string
  unread: number
}

export interface ServiceCategory {
  id: string
  name: string
  icon: React.ComponentType<{ className?: string }>
  description: string
  color: string
  borderColor: string
}

export function buildContacts(
  profilePictures: Record<string, string>,
  userIds: Record<string, string>,
  orgId?: string,
): Contact[] {
  if (orgId === DEF_JAM_ORG_ID) {
    return defJamContacts
  }

  return [
    {
      id: "1",
      name: "Adam Silver",
      role: "Entertainment Lawyer",
      company: "Silver & Associates",
      email: "adam@silverlaw.com",
      phone: "+1 (555) 123-4567",
      location: "Los Angeles, CA",
      avatar: profilePictures[userIds.ADAM_SILVER],
      tags: ["Legal", "Contracts", "IP Rights"],
      lastContact: "2 days ago",
      isStarred: true,
      type: "network",
    },
    {
      id: "2",
      name: "Roger Goodell",
      role: "Talent Manager",
      company: "Goodell Management",
      email: "roger@goodellmgmt.com",
      phone: "+1 (555) 234-5678",
      location: "New York, NY",
      avatar: profilePictures[userIds.ROGER_GOODELL],
      tags: ["Management", "Deals", "Strategy"],
      lastContact: "1 week ago",
      isStarred: false,
      type: "network",
    },
    {
      id: "3",
      name: "Sarah Chen",
      role: "Music Producer",
      company: "Chen Studios",
      email: "sarah@chenstudios.com",
      phone: "+1 (555) 345-6789",
      location: "Nashville, TN",
      avatar: undefined,
      tags: ["Production", "Recording", "Collaboration"],
      lastContact: "3 days ago",
      isStarred: true,
      type: "network",
    },
    {
      id: "4",
      name: "Marcus Johnson",
      role: "Accountant / Business Manager",
      company: "Johnson Financial",
      email: "marcus@johnsonfinancial.com",
      phone: "+1 (555) 456-7890",
      location: "Atlanta, GA",
      avatar: undefined,
      tags: ["Accounting", "Finance", "Tax"],
      lastContact: "1 month ago",
      isStarred: false,
      type: "network",
    },
    {
      id: "5",
      name: "Emily Rodriguez",
      role: "Booking Agent",
      company: "Live Nation",
      email: "emily@livenation.com",
      phone: "+1 (555) 567-8901",
      location: "Chicago, IL",
      avatar: undefined,
      tags: ["Agency", "Touring", "Events"],
      lastContact: "5 days ago",
      isStarred: false,
      type: "network",
    },
    {
      id: "6",
      name: "David Kim",
      role: "A&R Director",
      company: "Sony Music",
      email: "david@sonymusic.com",
      phone: "+1 (555) 678-9012",
      location: "Los Angeles, CA",
      avatar: undefined,
      tags: ["Label", "A&R", "Recording"],
      lastContact: "2 weeks ago",
      isStarred: true,
      type: "network",
    },
    {
      id: "7",
      name: "Lisa Thompson",
      role: "Marketing Director",
      company: "November",
      email: "lisa@november.com",
      phone: "+1 (555) 789-0123",
      location: "Los Angeles, CA",
      avatar: undefined,
      tags: ["Marketing", "Team"],
      lastContact: "1 day ago",
      isStarred: false,
      type: "organization",
    },
    {
      id: "8",
      name: "James Wilson",
      role: "Legal Counsel",
      company: "November",
      email: "james@november.com",
      phone: "+1 (555) 890-1234",
      location: "Los Angeles, CA",
      avatar: undefined,
      tags: ["Legal", "Team"],
      lastContact: "3 days ago",
      isStarred: false,
      type: "organization",
    },
  ]
}

export function buildConversations(
  profilePictures: Record<string, string>,
  userIds: Record<string, string>,
  orgId?: string,
): Conversation[] {
  if (orgId === DEF_JAM_ORG_ID) {
    return defJamConversations
  }

  return [
    {
      id: "1",
      contactId: "1",
      contactName: "Adam Silver",
      avatar: profilePictures[userIds.ADAM_SILVER],
      lastMessage: "Let me review the contract terms and get back to you by EOD.",
      timestamp: "2 hours ago",
      unread: 2,
    },
    {
      id: "2",
      contactId: "2",
      contactName: "Roger Goodell",
      avatar: profilePictures[userIds.ROGER_GOODELL],
      lastMessage: "Great! Let's schedule a call next week to discuss the tour dates.",
      timestamp: "1 day ago",
      unread: 0,
    },
    {
      id: "3",
      contactId: "3",
      contactName: "Sarah Chen",
      avatar: undefined,
      lastMessage: "I have studio time available in March if you want to book it.",
      timestamp: "3 days ago",
      unread: 1,
    },
  ]
}

export const serviceCategories: ServiceCategory[] = [
  {
    id: "legal",
    name: "Legal Services",
    icon: Briefcase,
    description: "Entertainment lawyers, contract specialists, IP attorneys",
    color: "from-blue-500/20 to-blue-600/20",
    borderColor: "border-blue-500/30",
  },
  {
    id: "accounting",
    name: "Accounting & Finance",
    icon: Building2,
    description: "Business managers, accountants, financial advisors",
    color: "from-green-500/20 to-green-600/20",
    borderColor: "border-green-500/30",
  },
  {
    id: "agency",
    name: "Agency & Booking",
    icon: Users,
    description: "Booking agents, talent agencies, tour managers",
    color: "from-purple-500/20 to-purple-600/20",
    borderColor: "border-purple-500/30",
  },
  {
    id: "production",
    name: "Production & Creative",
    icon: Sparkles,
    description: "Producers, engineers, creative directors",
    color: "from-yellow-500/20 to-yellow-600/20",
    borderColor: "border-yellow-500/30",
  },
]
